import React, {useEffect, useRef, useState, createRef} from 'react';
import FFTChartMerge from '@user-components/FFTChartMerge';
import {Grid, Box, IconButton, TextField, Button, MenuItem, Typography} from '@material-ui/core';
import {CheckCircle, Cancel} from '@material-ui/icons';
import {makeStyles} from "@material-ui/core/styles";
import ColorHelper from '@helper/ColorHelper';
import SocketProvider from '@user-provider/SocketProvider';
import MeasureHelper from '@helper/MeasureHelper';
import TimeCount from '@user-pages/exercise/common/TimeCount';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import StopIcon from '@material-ui/icons/Stop';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import Axios from 'axios';
import {showSnackbar} from '@helper/SnackbarHelper';
import Autocomplete from '@material-ui/lab/Autocomplete';

function StepInput({label, value, onChange, step = 0.1, width = 100, min, max, classes}) {
    const intervalRef = useRef(null)
    const timeoutRef = useRef(null)
    const valueRef = useRef(value)
    valueRef.current = value

    const roundValue = (v) => Math.round(v * 100) / 100

    const applyDelta = (delta) => {
        const next = roundValue(parseFloat(valueRef.current) + delta)
        let clamped = min !== undefined ? Math.max(min, next) : next
        clamped = max !== undefined ? Math.min(max, clamped) : clamped
        onChange(clamped)
    }

    const startRepeat = (delta) => {
        stopRepeat()
        applyDelta(delta)
        timeoutRef.current = setTimeout(() => {
            intervalRef.current = setInterval(() => {
                applyDelta(delta)
            }, 80)
        }, 300)
    }

    const stopRepeat = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }
    }

    useEffect(() => {
        return () => stopRepeat()
    }, [])

    return (
        <Box display="flex" alignItems="center">
            <TextField
                type="number"
                label={label}
                variant="outlined"
                size="small"
                value={value}
                onChange={(e) => {
                    let val = parseFloat(e.target.value) || 0
                    if (min !== undefined) val = Math.max(min, val)
                    if (max !== undefined) val = Math.min(max, val)
                    onChange(val)
                }}
                InputProps={{className: classes.input}}
                InputLabelProps={{className: classes.inputLabel, shrink: true}}
                inputProps={{step, min, max}}
                style={{width}}
            />
            <Box display="flex" flexDirection="column" style={{marginLeft: 2}}>
                <IconButton
                    size="small"
                    className={classes.stepBtn}
                    onMouseDown={() => startRepeat(step)}
                    onMouseUp={stopRepeat}
                    onMouseLeave={stopRepeat}
                >
                    <ArrowDropUpIcon style={{fontSize: 20}}/>
                </IconButton>
                <IconButton
                    size="small"
                    className={classes.stepBtn}
                    onMouseDown={() => startRepeat(-step)}
                    onMouseUp={stopRepeat}
                    onMouseLeave={stopRepeat}
                >
                    <ArrowDropDownIcon style={{fontSize: 20}}/>
                </IconButton>
            </Box>
        </Box>
    )
}

const {measureTypeToCode} = MeasureHelper
const MeasureHzType = 'measureBand'

const SOURCE_DEVICE = 'device'
const SOURCE_FILE = 'file'
const SOURCE_PRESCRIPTION = 'prescription'

const MeasureBandLocal = () => {
    const classes = useStyles()
    const measureRef = useRef()
    const timeCountRef = useRef()

    const [source, setSource] = useState(SOURCE_DEVICE)
    const [measureTime, setMeasureTime] = useState(120)
    const [highValue, setHighValue] = useState(0)
    const [lowValue, setLowValue] = useState(55)
    const [isRunning, setIsRunning] = useState(false)
    const [speed, setSpeed] = useState(1)
    const [amplification, setAmplification] = useState(1)
    const [noiseFilter, setNoiseFilter] = useState('A')
    const [initNotice, setInitNotice] = useState(false)
    const [fileReady, setFileReady] = useState(false)
    const [fileName, setFileName] = useState('')
    const [deviceConnected, setDeviceConnected] = useState(false)
    const [resolution, setResolution] = useState('normal')
    const [prescriptions, setPrescriptions] = useState([])
    const [selectedPrescriptionID, setSelectedPrescriptionID] = useState('')
    const [prescriptionDataReady, setPrescriptionDataReady] = useState(false)
    const speedOptions = [0.5, 1, 1.5, 2]
    const noiseFilterOptions = ['A', 'B', 'C', 'D']

    const isFileMode = source === SOURCE_FILE
    const isPrescriptionMode = source === SOURCE_PRESCRIPTION
    const canPlay = isFileMode ? fileReady : isPrescriptionMode ? prescriptionDataReady : deviceConnected

    useEffect(() => {
        if (!window.app) {
            window.app = {}
        }

        SocketProvider.connect(() => {}, window, MeasureHzType)

        SocketProvider.onMeasure = (data) => {
            if (measureRef.current) {
                measureRef.current.update(data)
            }
        }

        // 기기 연결 상태 체크: C# 클라이언트에게 물어봄
        const checkDeviceStatus = () => {
            try {
                if (window.BRHW?.isDeviceConnected) {
                    const result = window.BRHW.isDeviceConnected()
                    // CefSharp은 Promise를 반환할 수 있음
                    if (result && typeof result.then === 'function') {
                        result.then((connected) => setDeviceConnected(!!connected))
                    } else {
                        setDeviceConnected(!!result)
                    }
                }
            } catch (e) {
                setDeviceConnected(false)
            }
        }
        checkDeviceStatus()
        const deviceCheckInterval = setInterval(checkDeviceStatus, 3000)

        // C#에서 연결 상태가 변경되면 콜백으로도 받을 수 있음
        window.app.onDeviceStatusChanged = (connected) => {
            setDeviceConnected(!!connected)
        }

        window.app.onLocalFFTData = (data) => {
            try {
                const parsed = typeof data === 'string' ? JSON.parse(data) : data
                if (measureRef.current) {
                    measureRef.current.update(parsed)
                }
                // FFT 데이터의 time으로 타이머 업데이트
                if (parsed.time && timeCountRef.current) {
                    timeCountRef.current.update(parsed.time)
                }
                // 고해상도 모드: 10초 지나면 안내 팝업 해제
                if (parsed.time >= 10) {
                    setInitNotice(false)
                }
            } catch (e) {
                console.error('[MeasureBandLocal] FFT 데이터 파싱 오류:', e)
            }
        }

        window.app.onMeasureFileOpened = (name) => {
            console.log('[MeasureBandLocal] 파일 열림:', name)
            setFileReady(true)
            setFileName(name || '')
        }

        return () => {
            if (measureRef.current) {
                measureRef.current.clear()
            }
            SocketProvider.measureStop(true).then()
            clearInterval(deviceCheckInterval)
            if (window.app) {
                window.app.onLocalFFTData = null
                window.app.onMeasureFileOpened = null
                window.app.onDeviceStatusChanged = null
            }
        }
    }, [])

    const handleStart = async () => {
        if (isRunning) return
        measureRef.current.clear()
        timeCountRef.current.resetInterval()
        timeCountRef.current.update(0)

        // 양쪽 모드 모두 speed, resolution 전달
        try { if (window.BRHW?.setPlaySpeed) window.BRHW.setPlaySpeed(speed) } catch (e) {}
        try { if (window.BRHW?.setResolution) window.BRHW.setResolution(resolution) } catch (e) {}

        // 초반 10초 안내 팝업 (고해상도일 때만, FFT data.time >= 10 에서 해제)
        if (resolution === 'high') {
            setInitNotice(true)
        }

        if (isFileMode) {
            // 파일 모드: C#에 재생 시작 요청
            try {
                if (window.BRHW?.startMeasureFilePlay) {
                    window.BRHW.startMeasureFilePlay()
                }
            } catch (e) {
                console.error('[MeasureBandLocal] 파일 재생 오류:', e)
            }
            setIsRunning(true)
        } else if (isPrescriptionMode) {
            // 회차 모드: raw 데이터를 서버에서 가져와서 C# 클라이언트에 전달
            try {
                const res = await Axios.get(`/api/manage/prescriptions-test/${selectedPrescriptionID}/raw`)
                if (res.data && res.data.data && window.BRHW?.loadRawData) {
                    window.BRHW.loadRawData(res.data.data)
                }
                if (window.BRHW?.startMeasureFilePlay) {
                    window.BRHW.startMeasureFilePlay()
                }
            } catch (e) {
                console.error('[MeasureBandLocal] 회차 재생 오류:', e)
            }
            setIsRunning(true)
        } else {
            // 기기 모드: C# 브릿지로 기기 측정 시작
            try {
                if (window.BRHW?.deviceMeasureStart) {
                    window.BRHW.deviceMeasureStart(measureTime, 'rest')
                }
            } catch (e) {
                console.error('[MeasureBandLocal] 기기 측정 시작 오류:', e)
            }
            setIsRunning(true)
        }
    }

    const handleStop = async () => {
        if (!isRunning) return

        if (isFileMode || isPrescriptionMode) {
            try {
                if (window.BRHW?.stopMeasureFilePlay) {
                    window.BRHW.stopMeasureFilePlay()
                }
            } catch (e) {
                console.error('[MeasureBandLocal] 재생 중지 오류:', e)
            }
        } else {
            try {
                if (window.BRHW?.DeviceMeasureStop) {
                    window.BRHW.DeviceMeasureStop()
                }
            } catch (e) {
                console.error('[MeasureBandLocal] 기기 측정 중지 오류:', e)
            }
        }

        timeCountRef.current.pauseInterval()
        setIsRunning(false)
    }

    const handleSourceChange = async (newSource) => {
        if (isRunning) return
        setSource(newSource)
        setFileReady(false)
        setFileName('')
        setSelectedPrescriptionID('')
        setPrescriptionDataReady(false)
        try {
            if (window.BRHW?.setInputSource) {
                window.BRHW.setInputSource(newSource)
            }
        } catch (e) {
            console.error('[MeasureBandLocal] 입력소스 전송 오류:', e)
        }

        if (newSource === SOURCE_PRESCRIPTION) {
            try {
                const res = await Axios.get('/api/manage/prescriptions-test/ids')
                setPrescriptions(res.data.prescriptions || [])
            } catch (e) {
                console.error('[MeasureBandLocal] 회차 목록 조회 오류:', e)
                setPrescriptions([])
            }
        }
    }

    return (
        <Box style={{position: 'relative'}}>
            {/* 상단 컨트롤 바 */}
            <Box className={classes.statusBar}>
                <Grid container alignItems="center" spacing={2} style={{height: '100%', padding: '0 16px'}}>
                    {/* 소스 선택 */}
                    <Grid item>
                        <TextField
                            select
                            label="입력소스"
                            variant="outlined"
                            size="small"
                            value={source}
                            onChange={(e) => handleSourceChange(e.target.value)}
                            disabled={isRunning}
                            InputProps={{className: classes.input}}
                            InputLabelProps={{className: classes.inputLabel, shrink: true}}
                            SelectProps={{classes: {icon: classes.selectIcon}}}
                            style={{width: 150}}
                        >
                            <MenuItem value={SOURCE_DEVICE}>기기에서 측정</MenuItem>
                            <MenuItem value={SOURCE_FILE}>파일에서 가져오기</MenuItem>
                            <MenuItem value={SOURCE_PRESCRIPTION}>회차 선택</MenuItem>
                        </TextField>
                    </Grid>

                    {/* 회차 선택 모드: 회차 목록 (검색 가능) */}
                    {isPrescriptionMode && prescriptions.length > 0 && (
                        <Grid item>
                            <Autocomplete
                                options={prescriptions}
                                getOptionLabel={(option) => String(option.id)}
                                value={prescriptions.find(p => p.id === selectedPrescriptionID) || null}
                                onChange={async (e, newValue) => {
                                    const id = newValue ? newValue.id : ''
                                    setSelectedPrescriptionID(id)
                                    setPrescriptionDataReady(false)
                                    if (id) {
                                        try {
                                            const res = await Axios.get(`/api/manage/prescriptions-test/${id}/raw`)
                                            if (res.data && res.data.data) {
                                                if (window.BRHW?.loadRawData) {
                                                    window.BRHW.loadRawData(res.data.data)
                                                }
                                                setPrescriptionDataReady(true)
                                            } else {
                                                showSnackbar(`선택한 회차(${id})에 측정 정보가 없습니다\n뇌기능 측정 > EEG측정을 완료하면 볼 수 있습니다`, {variant: 'warning', autoHideDuration: 5000, style: {whiteSpace: 'pre-line'}})
                                            }
                                        } catch (ex) {
                                            showSnackbar(`선택한 회차(${id})에 측정 정보가 없습니다\n뇌기능 측정 > EEG측정을 완료하면 볼 수 있습니다`, {variant: 'warning', autoHideDuration: 5000, style: {whiteSpace: 'pre-line'}})
                                        }
                                    }
                                }}
                                disabled={isRunning}
                                size="small"
                                style={{width: 160}}
                                classes={{inputRoot: classes.autocompleteInput, popupIndicator: classes.selectIcon, clearIndicator: classes.selectIcon}}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="회차"
                                        variant="outlined"
                                        InputLabelProps={{className: classes.inputLabel, shrink: true}}
                                    />
                                )}
                            />
                        </Grid>
                    )}

                    {/* 측정방법 */}
                    <Grid item>
                        <TextField
                            select
                            label="측정방법"
                            variant="outlined"
                            size="small"
                            value={resolution}
                            onChange={(e) => {
                                setResolution(e.target.value)
                                try { if (window.BRHW?.setResolution) window.BRHW.setResolution(e.target.value) } catch (ex) {}
                            }}
                            disabled={isRunning}
                            InputProps={{className: classes.input}}
                            InputLabelProps={{className: classes.inputLabel, shrink: true}}
                            SelectProps={{classes: {icon: classes.selectIcon}}}
                            style={{width: 130}}
                        >
                            <MenuItem value="normal">일반 (1Hz)</MenuItem>
                            <MenuItem value="high">고해상도 (0.1Hz)</MenuItem>
                        </TextField>
                    </Grid>

                    {/* 기기 모드: 측정시간 */}
                    {source === SOURCE_DEVICE && (
                        <Grid item>
                            <TextField
                                type="number"
                                label="측정시간(초)"
                                variant="outlined"
                                size="small"
                                value={measureTime}
                                onChange={(e) => setMeasureTime(parseInt(e.target.value) || 0)}
                                disabled={isRunning}
                                InputProps={{className: classes.input}}
                                InputLabelProps={{className: classes.inputLabel}}
                                style={{width: 130}}
                            />
                        </Grid>
                    )}

                    {/* 파일 모드: 측정데이터 열기 */}
                    {isFileMode && (
                        <Grid item>
                            <Button
                                variant="contained"
                                className={classes.openFileBtn}
                                disabled={isRunning}
                                onClick={() => {
                                    try {
                                        if (window.BRHW?.openMeasureFile) {
                                            window.BRHW.openMeasureFile(speed, parseFloat(highValue) || 0, parseFloat(lowValue) || 0, parseFloat(amplification) || 0, noiseFilter)
                                        }
                                    } catch (e) {
                                        console.error('[MeasureBandLocal] 파일 열기 오류:', e)
                                    }
                                }}
                            >
                                {fileReady ? `📂 ${fileName}` : '측정데이터 열기'}
                            </Button>
                        </Grid>
                    )}

                    {/* 시작/중지 */}
                    <Grid item>
                        {!isRunning ? (
                            <IconButton
                                className={classes.startBtn}
                                onClick={handleStart}
                                disabled={!canPlay}
                            >
                                <PlayArrowIcon/>
                            </IconButton>
                        ) : (
                            <IconButton className={classes.stopBtn} onClick={handleStop}>
                                <StopIcon/>
                            </IconButton>
                        )}
                    </Grid>

                    {/* 타이머 */}
                    <Grid item style={{color: '#fff'}}>
                        <TimeCount ref={timeCountRef}/>
                    </Grid>

                    {/* 구분선 */}
                    <Grid item style={{paddingLeft: 24, paddingRight: 24}}>
                        <Box className={classes.divider}/>
                    </Grid>

                    {/* High / Low / 증폭 입력 */}
                    <Grid item>
                        <StepInput
                            label="하이패스"
                            value={highValue}
                            step={resolution === 'high' ? 0.1 : 1}
                            min={0}
                            classes={classes}
                            onChange={(v) => {
                                setHighValue(v)
                                try { if (window.BRHW?.setHighPass) window.BRHW.setHighPass(parseFloat(v) || 0) } catch (ex) {}
                            }}
                        />
                    </Grid>
                    <Grid item>
                        <StepInput
                            label="로우패스"
                            value={lowValue}
                            step={resolution === 'high' ? 0.1 : 1}
                            min={0}
                            max={55}
                            classes={classes}
                            onChange={(v) => {
                                setLowValue(v)
                                try { if (window.BRHW?.setLowPass) window.BRHW.setLowPass(parseFloat(v) || 0) } catch (ex) {}
                            }}
                        />
                    </Grid>
                    <Grid item>
                        <StepInput
                            label="증폭"
                            value={amplification}
                            min={1}
                            width={80}
                            classes={classes}
                            onChange={(v) => {
                                setAmplification(v)
                                try { if (window.BRHW?.setAmplification) window.BRHW.setAmplification(parseFloat(v) || 0) } catch (ex) {}
                            }}
                        />
                    </Grid>
                    <Grid item>
                        <TextField
                            select
                            label="노이즈필터"
                            variant="outlined"
                            size="small"
                            value={noiseFilter}
                            onChange={(e) => {
                                setNoiseFilter(e.target.value)
                                try { if (window.BRHW?.setNoiseFilter) window.BRHW.setNoiseFilter(e.target.value) } catch (ex) {}
                            }}
                            InputProps={{className: classes.input}}
                            InputLabelProps={{className: classes.inputLabel, shrink: true}}
                            SelectProps={{classes: {icon: classes.selectIcon}}}
                            style={{width: 100}}
                        >
                            {noiseFilterOptions.map((opt) => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* 초기화 */}
                    <Grid item>
                        <Button
                            variant="outlined"
                            className={classes.resetBtn}
                            onClick={() => {
                                setHighValue(0)
                                setLowValue(55)
                                setAmplification(1)
                                try { if (window.BRHW?.setHighPass) window.BRHW.setHighPass(0) } catch (ex) {}
                                try { if (window.BRHW?.setLowPass) window.BRHW.setLowPass(55) } catch (ex) {}
                                try { if (window.BRHW?.setAmplification) window.BRHW.setAmplification(1) } catch (ex) {}
                            }}
                        >
                            초기화
                        </Button>
                    </Grid>

                    {/* 속도 선택 */}
                    {speedOptions.map((s) => (
                        <Grid item key={s}>
                            <Button
                                variant={speed === s ? 'contained' : 'outlined'}
                                className={speed === s ? classes.speedBtnActive : classes.speedBtn}
                                onClick={() => {
                                    setSpeed(s)
                                    try {
                                        if (window.BRHW?.setPlaySpeed) {
                                            window.BRHW.setPlaySpeed(s)
                                        }
                                    } catch (e) {
                                        console.error('[MeasureBandLocal] 속도 전송 오류:', e)
                                    }
                                }}
                            >
                                {s}x
                            </Button>
                        </Grid>
                    ))}

                    {/* 새로고침 */}
                    <Grid item style={{marginLeft: 'auto'}}>
                        <Button
                            variant="contained"
                            className={classes.refreshBtn}
                            onClick={() => window.location.reload()}
                            startIcon={<RefreshIcon/>}
                        >
                            새로고침
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            {/* 연결 상태 - 툴바 아래 왼쪽 */}
            {source === SOURCE_DEVICE && (
                <Box className={classes.deviceStatus}>
                    {deviceConnected ? (
                        <CheckCircle style={{color: '#4CAF50', fontSize: 16, marginRight: 4}}/>
                    ) : (
                        <Cancel style={{color: '#f44336', fontSize: 16, marginRight: 4}}/>
                    )}
                    <Typography style={{color: '#000', fontSize: 12}}>
                        {deviceConnected ? '연결됨' : '신호없음'}
                    </Typography>
                </Box>
            )}

            {/* 초반 10초 안내 */}
            {initNotice && (
                <Box className={classes.initNotice}>
                    0.1Hz 단위로 분석하기 위해 10초 동안의 데이터를 수집합니다. 11초부터 측정 데이터입니다.
                </Box>
            )}

            {/* 차트 영역 */}
            <Box className={'innerViewport'} style={{marginTop: 0}}>
                <LocalRealtimeMeasure ref={measureRef}/>
            </Box>
        </Box>
    )
}

class LocalRealtimeMeasure extends React.Component {
    fftChartOptions = {
        graphWireFrame: false,
        graphWireFrameColor: 0x666666,
        hzSpacing: 10,
        hzCount: 60,
        cone: false,
        gridSize: 0.7,
        mvSize: 0.3,
        timeHzGrid: true,
        timeMvGrid: true,
        mvHzGrid: true,
        mvGuideLineXY: false,
        timeGuide: true,
        graphColors: ColorHelper.getColors(),
        height: 900,
        brainType: 'both',
        cameraType: 'side',
        noiseCode: 'A'
    }

    constructor(props) {
        super(props)
        this.fftChartRef = createRef(null)
    }

    update(data) {
        if (this.fftChartRef.current) {
            this.fftChartRef.current.addData(data.time, data.ch1FFT, data.ch2FFT)
        }
    }

    clear() {
        if (this.fftChartRef.current) {
            this.fftChartRef.current.clearCanvas()
        }
    }

    componentDidMount() {
        this.fftChartRef.current.initRender()
    }

    render() {
        return (
            <Box>
                <Box padding={2}>
                    <Grid container style={{width: '100%', position: 'relative'}}>
                        <Grid item xs={12} style={{position: 'relative'}}>
                            <FFTChartMerge label={'Brain'} options={this.fftChartOptions} ref={this.fftChartRef}/>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        )
    }
}

const statusBarHeight = 60

const useStyles = makeStyles(() => ({
    statusBar: {
        position: 'absolute',
        top: 0,
        paddingTop: 20,
        paddingBottom: 10,
        left: 0,
        width: '100%',
        height: statusBarHeight + 20,
        backgroundColor: 'rgba(0,0,0,0.75)',
        zIndex: 99,
    },
    input: {
        color: '#fff',
        height: 36,
        '& input': {
            textAlign: 'center',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.4)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.7)',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FFC004',
        },
        '&.Mui-disabled': {
            color: 'rgba(255,255,255,0.7)',
        },
    },
    inputLabel: {
        color: 'rgba(255,255,255,0.7)',
        '&.Mui-focused': {
            color: '#FFC004',
        },
    },
    startBtn: {
        backgroundColor: '#FF0005',
        color: '#fff',
        width: 40,
        height: 40,
        '&:hover': {
            backgroundColor: '#C90009',
        },
        '&.Mui-disabled': {
            backgroundColor: 'rgba(255,0,5,0.3)',
            color: 'rgba(255,255,255,0.4)',
        },
    },
    stopBtn: {
        backgroundColor: '#FF0005',
        color: '#fff',
        width: 40,
        height: 40,
        '&:hover': {
            backgroundColor: '#C90009',
        },
    },
    divider: {
        width: 1,
        height: 36,
        backgroundColor: 'rgba(255,255,255,0.3)',
    },
    openFileBtn: {
        backgroundColor: '#1976d2',
        color: '#fff',
        fontWeight: 'bold',
        height: 36,
        '&:hover': {
            backgroundColor: '#1565c0',
        },
        '&.Mui-disabled': {
            backgroundColor: 'rgba(25,118,210,0.3)',
            color: 'rgba(255,255,255,0.4)',
        },
    },
    resetBtn: {
        color: '#fff',
        borderColor: 'rgba(255,255,255,0.5)',
        height: 36,
        '&:hover': {
            borderColor: '#fff',
        },
    },
    refreshBtn: {
        backgroundColor: '#757575',
        color: '#fff',
        fontWeight: 'bold',
        height: 36,
        '&:hover': {
            backgroundColor: '#616161',
        },
    },
    stepBtn: {
        color: '#fff',
        padding: 0,
        width: 20,
        height: 18,
        '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.15)',
        },
    },
    selectIcon: {
        color: 'rgba(255,255,255,0.7)',
    },
    autocompleteInput: {
        color: '#fff',
        height: 36,
        padding: '0 8px !important',
        display: 'flex',
        alignItems: 'center',
        marginTop: -4,
        '& .MuiAutocomplete-input': {
            padding: '0 4px !important',
            color: '#fff',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.4)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.7)',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FFC004',
        },
    },
    deviceStatus: {
        position: 'absolute',
        top: statusBarHeight + 30,
        left: 8,
        display: 'flex',
        alignItems: 'center',
        zIndex: 100,
    },
    initNotice: {
        position: 'absolute',
        top: statusBarHeight + 30,
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: 'rgba(0,0,0,0.85)',
        color: '#FFC004',
        fontSize: 14,
        fontWeight: 'bold',
        padding: '10px 24px',
        borderRadius: 8,
        zIndex: 100,
        whiteSpace: 'nowrap',
    },
    speedBtn: {
        color: '#fff',
        borderColor: 'rgba(255,255,255,0.5)',
        height: 36,
        minWidth: 50,
        '&:hover': {
            borderColor: '#fff',
        },
    },
    speedBtnActive: {
        backgroundColor: '#FFC004',
        color: '#000',
        fontWeight: 'bold',
        height: 36,
        minWidth: 50,
        '&:hover': {
            backgroundColor: '#e0a800',
        },
    },
}))

export default MeasureBandLocal
